import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const WidgetV2 = ({
  children,
  title,
  actions,
  sxCard,
  sxContent,
  collapse,
  insideTab,
  initialExpanded,
  subheader,
}) => {
  const [expanded, setExpanded] = useState(initialExpanded);

  const getHeaderActions = () => (
    <>
      {actions
        .filter((a) => (a.show === undefined || !!a.show) && a.isComponent)
        .map((a) => a.component)}
      {actions
        .filter((a) => (a.show === undefined || !!a.show) && !a.isComponent)
        .map((a) => (
          <Button
            key={a.title}
            color="primary"
            variant="contained"
            onClick={a.onClick}
            sx={(theme) => ({
              mr: 2,
              fontWeight: 700,
              minWidth: 112,
              px: 2.5,
              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.42)}`,
              '&:hover:not(:disabled)': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                transform: 'translateY(-1px)',
              },
              transition: theme.transitions.create(['box-shadow', 'transform'], {
                duration: theme.transitions.duration.short,
              }),
            })}
          >
            {a.title}
          </Button>
        ))}
      {collapse && (
        <IconButton
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-label="show more"
          sx={{
            color: 'common.white',
            ml: 'auto',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: (theme) =>
              theme.transitions.create('transform', {
                duration: theme.transitions.duration.shortest,
              }),
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      )}
    </>
  );

  return (
    <Card
      sx={{
        boxShadow: 1,
        borderRadius: 1,
        border: (theme) => `0.5px solid ${theme.palette.divider}`,
        bgcolor: 'background.default',
        ...sxCard,
      }}
    >
      <CardHeader
        title={title}
        subheader={subheader}
        action={getHeaderActions()}
        sx={{
          position: 'relative',
          boxSizing: 'border-box',
          py: { xs: 1.75, sm: 2 },
          px: { xs: 2, sm: 2.5 },
          bgcolor: (theme) => theme.palette.neutral[900],
          borderBottom: (theme) =>
            `1px solid ${alpha(
              theme.palette.mode === 'dark'
                ? theme.palette.neutral[50]
                : theme.palette.neutral[900],
              0.08
            )}`,
          '&::after': {
            background: (theme) =>
              `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.35)} 55%, transparent 100%)`,
            bottom: 0,
            content: '""',
            height: 3,
            left: 0,
            opacity: 0.9,
            position: 'absolute',
            width: '38%',
          },
          '& .MuiCardHeader-title': {
            color: 'common.white',
            fontWeight: 600,
            letterSpacing: 0.3,
            ...(insideTab && { fontSize: 14 }),
          },
          '& .MuiCardHeader-subheader': {
            color: (theme) => theme.palette.neutral[400],
          },
        }}
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, ...sxContent }}>
          {children}
        </CardContent>
      </Collapse>
    </Card>
  );
};

WidgetV2.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  actions: PropTypes.array,
  sxCard: PropTypes.object,
  sxContent: PropTypes.object,
  collapse: PropTypes.bool,
  insideTab: PropTypes.bool,
  initialExpanded: PropTypes.bool,
  subheader: PropTypes.node,
};

WidgetV2.defaultProps = {
  actions: [],
  sxCard: {},
  sxContent: {},
  collapse: false,
  insideTab: false,
  initialExpanded: true,
  subheader: null,
};

export default WidgetV2;
